// buildBloomStatus — NL summary of previous sessions for Anna's system prompt
//
// Takes completed sessions for a topic and builds a string that gives Anna
// context about the student's learning history. Returns empty string if no
// previous completed sessions exist (first-time session).

import type { TeachingForm } from '../types';

interface SessionSummary {
  teachingForm: TeachingForm;
  startedAt: Date | string;
  bloomLevelReached?: string;
}

/**
 * Build a natural-language bloom status string from previous sessions.
 *
 * @param sessions - Completed sessions for this topic (newest first, max 3)
 * @param conceptMapNodeCount - Number of nodes in the current concept map
 * @param currentTeachingForm - Teaching form of the session being started (optional)
 * @returns A string for the system prompt, or empty string if no history
 */
export function buildBloomStatus(
  sessions: SessionSummary[],
  conceptMapNodeCount: number,
  currentTeachingForm?: string,
): string {
  if (sessions.length === 0) return '';

  const lines: string[] = ['Previous sessions for this topic:'];

  for (const session of sessions) {
    const date = session.startedAt instanceof Date
      ? session.startedAt
      : new Date(session.startedAt);
    const daysAgo = Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    const timeLabel = daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`;
    const levelLabel = session.bloomLevelReached
      ? `reached '${session.bloomLevelReached}' level`
      : 'no bloom level recorded';

    lines.push(`- ${capitalize(session.teachingForm)} (${timeLabel}): ${levelLabel}`);
  }

  if (conceptMapNodeCount > 0) {
    lines.push(`Concept map has ${conceptMapNodeCount} nodes.`);
  }

  // Exam history context
  const examSessions = sessions.filter(s => s.teachingForm === 'exam');
  const nonExamSessions = sessions.filter(s => s.teachingForm !== 'exam');

  if (examSessions.length > 0) {
    const bloomOrder = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
    const highestExamBloom = examSessions
      .map(s => s.bloomLevelReached)
      .filter(Boolean)
      .reduce((highest, current) => {
        return bloomOrder.indexOf(current!) > bloomOrder.indexOf(highest!) ? current : highest;
      }, examSessions[0].bloomLevelReached);

    lines.push(`Exam training: ${examSessions.length} session(s), highest bloom level: ${highestExamBloom || 'none recorded'}.`);

    if (nonExamSessions.length > 0) {
      lines.push(`The student also has ${nonExamSessions.length} non-exam session(s) on this topic.`);
    }
  }

  lines.push(
    'This is a returning student. Open with a brief context sentence ' +
    'referencing their previous level in natural language (not Bloom ' +
    'terminology: use "basic understanding" for recognize/understand, ' +
    '"applying concepts" for apply/analyze, "deep understanding" for ' +
    'evaluate/create). Ask an open question about direction. ' +
    'Do not lecture about their progress — just acknowledge where they ' +
    'are and invite them to choose direction.',
  );

  if (currentTeachingForm === 'exam' && examSessions.length > 0) {
    lines.push(
      'This is a returning exam student. Reference their exam history briefly: ' +
      'if they reached formulation level last time, suggest trying free presentation. ' +
      'If they are new to exam training but have dialog experience, acknowledge that and start gently. ' +
      'Keep it to one sentence — do not lecture about their progress.',
    );
  }

  return lines.join('\n');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
